// Las MENCIONES del compositor — ADR-2026-09-21-la-arroba-menciona-los-adjuntos. Escribir
// `@` en la caja del Agente abre un menú con lo que está enganchado (productos, modelos,
// imágenes, poses, fondos, archivos…); elegir uno deja `@Nombre` en el texto, que es como
// el modelo ya recibe cada adjunto en su contexto (los productos y modelos por nombre, las
// poses y fondos etiquetados, las imágenes sin nombre numeradas en orden). Puro, para
// testear el token (que acepta espacios: los nombres los tienen), el filtro y el reemplazo.

import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { etiquetaDeAdjunto } from '../../pages/v2/etiquetaDeAdjunto';
import { mencionesEn, nombreDeMencion } from '../../pages/v2/mencionesDeAdjuntos';

export interface TokenDeMencion {
  /** Índice del `@` en el texto. */
  desde: number;
  /** Lo escrito después del `@`, hasta el cursor (puede tener espacios). */
  consulta: string;
}

/**
 * El token `@...` sobre el que está el cursor: un `@` al principio o después de un espacio,
 * salto o paréntesis, seguido de letras, números y espacios hasta el cursor. Los espacios
 * entran porque los nombres los tienen ("Remera oversize"); el menú se cierra solo cuando
 * ya nada coincide (`opcionesDeMencion` vacío) — eso lo decide quien lo dibuja.
 */
export function tokenDeMencionEn(texto: string, cursor: number): TokenDeMencion | null {
  const hasta = Math.max(0, Math.min(cursor, texto.length));
  const antes = texto.slice(0, hasta);
  const m = /(^|[\s(])@([\p{L}\p{N}][\p{L}\p{N} ]*|)$/u.exec(antes);
  if (!m) return null;
  return { desde: hasta - m[2].length - 1, consulta: m[2] };
}

export interface OpcionDeMencion {
  id: string;
  /** Lo que queda en el texto después del `@`. */
  nombre: string;
  /** Qué es: Producto, Modelo, Imagen, Pose… (`etiquetaDeAdjunto`). */
  etiqueta: string;
  kind: ChatAttachment['kind'];
  miniatura: string | null;
}

const sinAcentos = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export { mencionesEn, nombreDeMencion };

const miniaturaDe = (a: ChatAttachment): string | null => {
  switch (a.kind) {
    case 'image':
      return a.previewUrl;
    case 'product':
    case 'model':
    case 'referencia':
    case 'moodboard':
      return a.imageUrl;
    default:
      return null;
  }
};

/** Los adjuntos que coinciden con la consulta (por nombre o por qué son), en su orden. */
export function opcionesDeMencion(adjuntos: readonly ChatAttachment[], consulta: string): OpcionDeMencion[] {
  const q = sinAcentos(consulta.trim());
  const out: OpcionDeMencion[] = [];
  for (const a of adjuntos) {
    const nombre = nombreDeMencion(a, adjuntos);
    const etiqueta = etiquetaDeAdjunto(a);
    if (q && !sinAcentos(nombre).includes(q) && !sinAcentos(etiqueta).includes(q)) continue;
    out.push({ id: a.id, nombre, etiqueta, kind: a.kind, miniatura: miniaturaDe(a) });
  }
  return out;
}

/** El texto con el token reemplazado por `@Nombre` y un espacio detrás (el que ya había, si
 *  había), y el cursor después de ese espacio. */
export function conMencionInsertada(texto: string, token: TokenDeMencion, nombre: string): { texto: string; cursor: number } {
  const hasta = token.desde + 1 + token.consulta.length;
  const resto = texto.slice(hasta);
  const pegado = resto.startsWith(' ') ? '' : ' ';
  const insertado = `@${nombre}`;
  return { texto: `${texto.slice(0, token.desde)}${insertado}${pegado}${resto}`, cursor: token.desde + insertado.length + 1 };
}
